import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { entities, modules } from "byzantine";

export type WirePaymentDetails = {
  description: string;
  fromAccount: string;
  internalNote: string;
};

type DomesticWireImportContextType = {
  csvResponse: API.WireImport.UploadCSV.Response | null;
  parsedRows: API.WireImport.CSVParsedRow[] | null;
  organizationFileUuid: string | null;
  wireImportBatchId: API.WireImport.WireImportBatchId | null;
  paymentDetails: WirePaymentDetails;
  validPayments: API.WireImport.CSVParsedRow[];
  totalAmount: number;
  setCsvResponse: (data: API.WireImport.UploadCSV.Response) => void;
  setParsedRows: (rows: API.WireImport.CSVParsedRow[]) => void;
  setWireImportBatchId: (id: API.WireImport.WireImportBatchId) => void;
  updateParsedRows: (
    updater: (
      rows: API.WireImport.CSVParsedRow[]
    ) => API.WireImport.CSVParsedRow[]
  ) => void;
  setPaymentDetails: (details: WirePaymentDetails) => void;
  clearImport: () => void;
};

const DEFAULT_PAYMENT_DETAILS: WirePaymentDetails = {
  description: "",
  fromAccount: "",
  internalNote: "",
};

const DomesticWireImportContext =
  createContext<DomesticWireImportContextType>({
    csvResponse: null,
    parsedRows: null,
    organizationFileUuid: null,
    wireImportBatchId: null,
    paymentDetails: DEFAULT_PAYMENT_DETAILS,
    validPayments: [],
    totalAmount: 0,
    setCsvResponse: () => {},
    setParsedRows: () => {},
    setWireImportBatchId: () => {},
    updateParsedRows: () => {},
    setPaymentDetails: () => {},
    clearImport: () => {},
  });

export const DomesticWireImportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [csvResponse, setCsvResponseState] =
    useState<API.WireImport.UploadCSV.Response | null>(null);
  const [parsedRows, setParsedRowsState] = useState<
    API.WireImport.CSVParsedRow[] | null
  >(null);
  const [organizationFileUuid, setOrganizationFileUuid] = useState<
    string | null
  >(null);
  const [wireImportBatchId, setWireImportBatchId] = useState<
    API.WireImport.WireImportBatchId | null
  >(null);
  const [paymentDetails, setPaymentDetailsState] =
    useState<WirePaymentDetails>(DEFAULT_PAYMENT_DETAILS);

  // Fetch recipients on mount so useRecipients() has data in downstream screens.
  // includeHidden ensures import-created (hidden) recipients are available.
  const { send: fetchRecipients } =
    entities.recipients.useFetchRecipients({ includeHidden: true });
  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  const setCsvResponse = useCallback(
    (data: API.WireImport.UploadCSV.Response) => {
      setCsvResponseState(data);
      setOrganizationFileUuid(data.organization_file_uuid);
    },
    []
  );

  const setParsedRows = useCallback(
    (rows: API.WireImport.CSVParsedRow[]) => {
      setParsedRowsState(rows);
    },
    []
  );

  const updateParsedRows = useCallback(
    (
      updater: (
        rows: API.WireImport.CSVParsedRow[]
      ) => API.WireImport.CSVParsedRow[]
    ) => {
      setParsedRowsState((prev) => {
        if (!prev) return prev;
        return updater(prev);
      });
    },
    []
  );

  const setPaymentDetails = useCallback(
    (details: WirePaymentDetails) => {
      setPaymentDetailsState(details);
    },
    []
  );

  const clearImport = useCallback(() => {
    setCsvResponseState(null);
    setParsedRowsState(null);
    setOrganizationFileUuid(null);
    setWireImportBatchId(null);
    setPaymentDetailsState(DEFAULT_PAYMENT_DETAILS);
  }, []);

  const wireReasons = modules.wireTemplates.useWireReasons();

  const validPayments = useMemo(() => {
    if (!parsedRows) return [];
    return parsedRows.filter(
      (row) =>
        row.recipient !== null &&
        Number.isFinite(row.amount ?? 0) &&
        (row.amount ?? 0) > 0 &&
        Boolean(row.memo?.trim()) &&
        (wireReasons.length === 0 || Boolean(row.wire_reason?.trim()))
    );
  }, [parsedRows, wireReasons]);

  const totalAmount = useMemo(
    () => validPayments.reduce((sum, row) => sum + (row.amount ?? 0), 0),
    [validPayments]
  );

  return (
    <DomesticWireImportContext.Provider
      value={{
        csvResponse,
        parsedRows,
        organizationFileUuid,
        wireImportBatchId,
        paymentDetails,
        validPayments,
        totalAmount,
        setCsvResponse,
        setParsedRows,
        setWireImportBatchId,
        updateParsedRows,
        setPaymentDetails,
        clearImport,
      }}
    >
      {children}
    </DomesticWireImportContext.Provider>
  );
};

export const useDomesticWireImportContext = () =>
  useContext(DomesticWireImportContext);

export default DomesticWireImportContext;
